import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useSnackbar } from "../../hooks/useSnackbar";
import { Badge, Panel, FormGrid, ListGrid, PrimaryButton, GhostButton } from "./ui";
import { formatKurus, DP } from "./constants";
import {
  getPersonal,
  createPersonal,
  updatePersonal,
  deletePersonal,
} from "../../api/digitalPayAPI";
import {
  PERSONAL_TEMPLATE,
  BALANCE_TEMPLATE,
  downloadTemplate,
  readSheet,
  missingHeaders,
} from "./excelTemplates";
import {
  createAndDownloadExcel,
  generateExcelFilename,
} from "../../utils/excelExportUtils";
import { isSuperAdminUser } from "./roleUtils";

const EMPTY = {
  idpersonal: "",
  name: "",
  surname: "",
  regNumber: "",
  cardNo: "",
  department_iddepartment: "",
};

const EMPTY_FILTERS = {
  regNumber: "",
  name: "",
  surname: "",
  cardNo: "",
  department: "",
  firm: "",
  cardBalance: "",
  cardIsgBalance: "",
  isActive: "",
};

const normalizeFilterValue = (value) =>
  String(value ?? "").trim().toLocaleLowerCase("tr-TR");

export default function PersonalPanel({ lookups, onChanged }) {
  const { firms = [], departments = [] } = lookups;
  const canSelectFirm = isSuperAdminUser();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [form, setForm] = useState(EMPTY);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulk, setBulk] = useState(null); // { done, total }
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await getPersonal());
    } catch (e) {
      showSnackbar(e.message || "Personel listesi alınamadı", "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const departmentById = useMemo(
    () => new Map(departments.map((d) => [Number(d.iddepartment), d])),
    [departments]
  );
  const firmById = useMemo(
    () => new Map(firms.map((f) => [Number(f.idfirm), f])),
    [firms]
  );

  const filteredRows = useMemo(() => {
    const matches = (value, filter) =>
      !filter || normalizeFilterValue(value).includes(normalizeFilterValue(filter));

    return rows
      .filter((row) => {
        const departmentName =
          departmentById.get(Number(row.department_iddepartment))?.name ?? "";
        const firmName =
          firmById.get(Number(row.firm_idfirm))?.name ?? row.firmName ?? "";

        return (
          matches(row.regNumber, filters.regNumber) &&
          matches(row.name, filters.name) &&
          matches(row.surname, filters.surname) &&
          matches(row.cardNo, filters.cardNo) &&
          matches(departmentName, filters.department) &&
          matches(firmName, filters.firm) &&
          matches(formatKurus(row.cardBalance), filters.cardBalance) &&
          matches(formatKurus(row.cardIsgBalance), filters.cardIsgBalance) &&
          matches(Number(row.isActive) === 1 ? "Aktif" : "Pasif", filters.isActive)
        );
      })
      .sort((a, b) =>
        `${a.name ?? ""} ${a.surname ?? ""}`.localeCompare(
          `${b.name ?? ""} ${b.surname ?? ""}`,
          "tr",
          { sensitivity: "base" }
        )
      );
  }, [departmentById, filters, firmById, rows]);

  // Firma, departmandan türetilir — şablonda firma sütunu yok, departman var.
  const firmOfDepartment = useCallback(
    (deptId) => departmentById.get(Number(deptId))?.firm_idfirm ?? 0,
    [departmentById]
  );

  const reset = () => setForm(EMPTY);

  const openCreateDialog = () => {
    reset();
    setDialogOpen(true);
  };

  const openUpdateDialog = (row) => {
    setForm({
      idpersonal: row.idpersonal,
      name: row.name ?? "",
      surname: row.surname ?? "",
      regNumber: row.regNumber ?? "",
      cardNo: row.cardNo ?? "",
      department_iddepartment: row.department_iddepartment ?? "",
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    reset();
  };

  const handleSave = async () => {
    if (!form.name || !form.surname || !form.cardNo) {
      showSnackbar("Ad, soyad ve kart no zorunlu", "error");
      return;
    }
    const deptId = Number(form.department_iddepartment) || 0;
    const dto = {
      name: form.name,
      surname: form.surname,
      regNumber: form.regNumber,
      cardNo: form.cardNo,
      department_iddepartment: deptId,
      firm_idfirm: firmOfDepartment(deptId),
      isActive: 1,
    };
    setSaving(true);
    try {
      if (form.idpersonal) {
        await updatePersonal({ ...dto, idpersonal: Number(form.idpersonal) });
        showSnackbar("Personel güncellendi", "success");
      } else {
        // Bakiye sıfır başlar; yalnızca bakiye yükleme ile artar.
        await createPersonal({ ...dto, cardBalance: 0, cardIsgBalance: 0 });
        showSnackbar("Personel eklendi", "success");
      }
      setDialogOpen(false);
      reset();
      load();
      onChanged?.();
    } catch (e) {
      showSnackbar(e.message || "Kaydedilemedi", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deletePersonal(id);
      showSnackbar("Personel silindi", "success");
      if (Number(form.idpersonal) === Number(id)) {
        setDialogOpen(false);
        reset();
      }
      load();
    } catch (e) {
      showSnackbar(e.message || "Silinemedi", "error");
    }
  };

  // --- Toplu yükleme (PersonelYüklemeŞablonu.xlsx) ---
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // aynı dosya tekrar seçilebilsin
    if (!file) return;

    let sheet;
    try {
      sheet = await readSheet(file);
    } catch (err) {
      showSnackbar(err.message, "error");
      return;
    }

    const missing = missingHeaders(sheet, PERSONAL_TEMPLATE);
    if (missing.length) {
      showSnackbar(`Şablon uyumsuz. Eksik sütun: ${missing.join(", ")}`, "error");
      return;
    }

    let ok = 0;
    const failed = [];
    setBulk({ done: 0, total: sheet.length });

    // Sıralı gönderiyoruz: backend'i aynı anda 200 istekle boğmamak için.
    // ponytail: satır sayısı çok büyürse backend'e toplu uç eklenmeli.
    for (let i = 0; i < sheet.length; i++) {
      const r = sheet[i];
      const deptId = Number(r.DepartmanId) || 0;
      try {
        await createPersonal({
          regNumber: String(r["Sicil No"]),
          cardNo: String(r["Kart No"]),
          name: String(r["Adı"]),
          surname: String(r["Soyadı"]),
          department_iddepartment: deptId,
          firm_idfirm: firmOfDepartment(deptId),
          cardBalance: 0,
          cardIsgBalance: 0,
          isActive: 1,
        });
        ok++;
      } catch (err) {
        failed.push(`${r["Sicil No"]}: ${err.message}`);
      }
      setBulk({ done: i + 1, total: sheet.length });
    }

    setBulk(null);
    load();
    onChanged?.();

    if (failed.length) {
      // Sessiz kısmi başarı olmasın: hatalı satırlar konsola da yazılır.
      console.warn("[Personel toplu yükleme] hatalı satırlar:", failed);
      showSnackbar(
        `Toplu yükleme bitti. Başarılı: ${ok}, hatalı: ${failed.length}. İlk hata: ${failed[0]}`,
        "warning"
      );
    } else {
      showSnackbar(`Toplu yükleme tamamlandı. ${ok} personel eklendi.`, "success");
    }
  };

  const filterHeader = (label, field) => (
    <Box sx={{ width: "100%", minWidth: 0, py: 0.5 }}>
      <Typography
        sx={{
          fontSize: "0.69rem",
          fontWeight: 720,
          lineHeight: 1.2,
          color: DP.textMuted,
          mb: 0.6,
        }}
      >
        {label}
      </Typography>
      <TextField
        value={filters[field]}
        onChange={(event) =>
          setFilters((current) => ({ ...current, [field]: event.target.value }))
        }
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        placeholder="Filtrele"
        variant="standard"
        fullWidth
        inputProps={{ "aria-label": `${label} filtresi` }}
        sx={{
          "& .MuiInputBase-root": { fontSize: "0.72rem" },
          "& .MuiInputBase-input": { py: 0.35 },
        }}
      />
    </Box>
  );

  const columns = [
    {
      field: "regNumber",
      headerName: "Sicil No",
      flex: 1,
      minWidth: 120,
      renderHeader: () => filterHeader("Sicil No", "regNumber"),
    },
    {
      field: "name",
      headerName: "Ad",
      flex: 1,
      minWidth: 115,
      sortComparator: (a, b) =>
        String(a ?? "").localeCompare(String(b ?? ""), "tr", {
          sensitivity: "base",
        }),
      renderHeader: () => filterHeader("Ad", "name"),
    },
    {
      field: "surname",
      headerName: "Soyad",
      flex: 1,
      minWidth: 115,
      renderHeader: () => filterHeader("Soyad", "surname"),
    },
    {
      field: "cardNo",
      headerName: "Kart No",
      flex: 1,
      minWidth: 125,
      sortComparator: (a, b) =>
        String(a ?? "").localeCompare(String(b ?? ""), "tr", {
          numeric: true,
        }),
      renderHeader: () => filterHeader("Kart No", "cardNo"),
    },
    {
      field: "departmentName",
      headerName: "Departman",
      flex: 1,
      minWidth: 135,
      valueGetter: (_value, row) =>
        departmentById.get(Number(row.department_iddepartment))?.name ?? "",
      renderHeader: () => filterHeader("Departman", "department"),
    },
    ...(canSelectFirm
      ? [
          {
            field: "firmName",
            headerName: "Firma",
            flex: 1,
            minWidth: 125,
            valueGetter: (value, row) =>
              value ?? firmById.get(Number(row.firm_idfirm))?.name ?? "",
            renderHeader: () => filterHeader("Firma", "firm"),
          },
        ]
      : []),
    {
      field: "cardBalance",
      headerName: "Standart B.",
      width: 110,
      cellClassName: "dp-balance-cell",
      valueFormatter: (v) => formatKurus(v),
      renderHeader: () => filterHeader("Standart B.", "cardBalance"),
    },
    {
      field: "cardIsgBalance",
      headerName: "İSG B.",
      width: 100,
      cellClassName: "dp-balance-cell",
      valueFormatter: (v) => formatKurus(v),
      renderHeader: () => filterHeader("İSG B.", "cardIsgBalance"),
    },
    {
      field: "isActive",
      headerName: "Durum",
      width: 95,
      valueFormatter: (value) => (Number(value) === 1 ? "Aktif" : "Pasif"),
      renderCell: (params) => (
        <Badge tone={Number(params.value) === 1 ? "success" : "neutral"}>
          {Number(params.value) === 1 ? "Aktif" : "Pasif"}
        </Badge>
      ),
      renderHeader: () => filterHeader("Durum", "isActive"),
    },
    {
      field: "__actions",
      type: "actions",
      headerName: "",
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon fontSize="small" />}
          label="Güncelle"
          onClick={(e) => {
            e.stopPropagation();
            openUpdateDialog(params.row);
          }}
        />,
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon fontSize="small" />}
          label="Sil"
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(params.row.idpersonal);
          }}
        />,
      ],
    },
  ];

  const handleExport = () => {
    // Personel dışa aktarımı, Toplu Bakiye Yükleme ekranının beklediği
    // şablonla birebir aynı olmalı. Böylece kullanıcı yalnızca kuruş
    // cinsindeki bakiye değerini değiştirip dosyayı doğrudan yükleyebilir.
    const exportColumns = [
      { field: "regNumber", headerName: BALANCE_TEMPLATE.headers[0] },
      { field: "name", headerName: BALANCE_TEMPLATE.headers[1] },
      { field: "surname", headerName: BALANCE_TEMPLATE.headers[2] },
      { field: "cardBalance", headerName: BALANCE_TEMPLATE.headers[3] },
    ];
    const result = createAndDownloadExcel(
      filteredRows,
      exportColumns,
      BALANCE_TEMPLATE.sheetName,
      generateExcelFilename("Bakiye_Yukleme"),
      {
        valueTransformer: (value, column) => {
          if (column.field === "cardBalance") {
            const balance = Number(value);
            return Number.isFinite(balance) ? Math.round(balance) : 0;
          }
          return value ?? "";
        },
      }
    );

    if (result.success) {
      showSnackbar(`İndirildi: ${result.fileName}`, "success");
    } else {
      showSnackbar(result.error || "Dışa aktarılamadı", "error");
    }
  };

  return (
    <>
      <Panel
        title="Toplu Personel Yükleme"
        sub="Personel Ekle butonundan tekil, Toplu Personel Yükle butonundan Excel formatında toplu personel yükleyebilirsiniz."
        action={
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: { xs: "wrap", lg: "nowrap" },
            }}
          >
            <PrimaryButton
              startIcon={<PersonAddIcon />}
              onClick={openCreateDialog}
              disabled={!!bulk}
            >
              Personel Ekle
            </PrimaryButton>
            <PrimaryButton
              component="label"
              startIcon={<UploadFileIcon />}
              disabled={!!bulk}
              sx={{
                bgcolor: DP.success,
                boxShadow: "0 8px 18px rgba(16,185,129,.22)",
                "&:hover": { bgcolor: "#059669" },
              }}
            >
              Toplu Personel Yükle
              <input type="file" accept=".xlsx,.xls" hidden onChange={handleUpload} />
            </PrimaryButton>
            <GhostButton
              startIcon={<DownloadIcon />}
              onClick={() => downloadTemplate(PERSONAL_TEMPLATE)}
              sx={{
                color: "#64748b",
                borderColor: "#cbd5e1",
                bgcolor: "#f8fafc",
                "&:hover": {
                  color: "#334155",
                  borderColor: "#94a3b8",
                  bgcolor: "#f1f5f9",
                },
              }}
            >
              Şablonu İndir
            </GhostButton>
          </Box>
        }
      >
        {bulk && (
          <Box>
            <Typography sx={{ fontSize: "0.85rem", color: DP.textMuted, mb: 0.8 }}>
              Yükleniyor… {bulk.done} / {bulk.total}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(bulk.done / bulk.total) * 100}
              sx={{ borderRadius: 999 }}
            />
          </Box>
        )}
      </Panel>

      <Panel
        title="Personel Listesi"
        sub={`${filteredRows.length} personel · Güncellemek için satırdaki düzenleme simgesini kullanın.`}
        action={
          <Box sx={{ display: "flex", gap: 1 }}>
            <GhostButton startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
              Yenile
            </GhostButton>
            <GhostButton startIcon={<FileDownloadIcon />} onClick={handleExport}>
              Dışa Aktar
            </GhostButton>
          </Box>
        }
      >
        <ListGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(r) => r.idpersonal}
          loading={loading}
          height={520}
          columnHeaderHeight={82}
          initialSortModel={[{ field: "name", sort: "asc" }]}
        />
      </Panel>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>
          {form.idpersonal ? "Personel Güncelle" : "Personel Ekle"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ color: DP.textMuted, fontSize: "0.82rem", mb: 2 }}>
            Firma, seçilen departmandan otomatik belirlenir.
          </Typography>
          <FormGrid>
            <TextField
              label="Adı"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              size="small"
              required
              fullWidth
            />
            <TextField
              label="Soyadı"
              value={form.surname}
              onChange={(e) => setForm({ ...form, surname: e.target.value })}
              size="small"
              required
              fullWidth
            />
            <TextField
              label="Sicil No"
              value={form.regNumber}
              onChange={(e) => setForm({ ...form, regNumber: e.target.value })}
              size="small"
              fullWidth
            />
            <TextField
              label="Kart No"
              value={form.cardNo}
              onChange={(e) => setForm({ ...form, cardNo: e.target.value })}
              size="small"
              required
              fullWidth
            />
            <TextField
              select
              label="Departman"
              value={form.department_iddepartment}
              onChange={(e) =>
                setForm({ ...form, department_iddepartment: e.target.value })
              }
              size="small"
              fullWidth
              helperText={
                form.department_iddepartment
                  ? `Firma: ${firmById.get(firmOfDepartment(form.department_iddepartment))?.name ?? "-"}`
                  : " "
              }
            >
              {departments.map((department) => (
                <MenuItem key={department.iddepartment} value={department.iddepartment}>
                  {department.name}
                </MenuItem>
              ))}
            </TextField>
          </FormGrid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <GhostButton onClick={closeDialog} disabled={saving}>
            İptal
          </GhostButton>
          <PrimaryButton onClick={handleSave} disabled={saving}>
            {form.idpersonal ? "Güncelle" : "Kaydet"}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
